// @flow
import React, { Component, Fragment, type ElementConfig } from 'react';
import { Trans } from '@lingui/react';
import { injectStripe } from 'react-stripe-elements';
import styled, { type StyledComponent } from 'styled-components';

import { snakeify } from '@rover/utilities/object-utilities';
import reportError from '@rover/utilities/reportError';

import { MQ, Spacing, DSToken } from '@rover/react-lib/styles';
import type { Address } from '@rover/react-lib/types';
import { isValidAddress } from '@rover/react-lib/utils/address';
import AddressForm from '@rover/react-lib/components/forms/AddressForm';
import addressFactory from '@rover/react-lib/factories/addressFactory';

import Button, { ButtonType } from '@rover/react-lib/components/buttons/Button';
import LoadingButton from '@rover/react-lib/components/buttons/LoadingButton';
import Form from '@rover/react-lib/components/utils/Form';
import Alert from '@rover/react-lib/components/notifications/Alert';
import StripeProvider from '@rover/react-lib/components/utils/StripeProvider';
import { Wrapper as FormGroup } from '@rover/react-lib/components/formFields/FormGroup/FormGroup';
import AnimatedSpinner from '@rover/react-lib/components/typography/AnimatedSpinner';

import AddressCheckbox from '../CreditCardForm/BraintreeCreditCardForm/AddressCheckbox';

import StripeCreditCardNameField from './StripeCreditCardNameField';
import StripeCreditCardNumberField from './StripeCreditCardNumberField';
import StripeCreditCardExpiryField from './StripeCreditCardExpiryField';
import StripeCreditCardCVCField from './StripeCreditCardCVCField';

const StyledForm: StyledComponent<React$ElementConfig<typeof Form>, {}, any> = styled(Form)`
  position: relative;
`;

const CreditCardFormFlexWrapper: StyledComponent<{}> = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const CVCExpiryResponsiveRow: StyledComponent<{}> = styled.div`
  display: flex;
  flex: 1 100%;

  ${MQ.NOT_XS} {
    flex: 1 100px;
    margin-left: ${Spacing.M};
  }

  ${FormGroup} {
    flex: 1 1 50%;

    &:first-child {
      margin-right: ${Spacing.S};
    }
    &:last-child {
      margin-left: ${Spacing.S};
    }
  }
`;

type StyledAnimatedSpinnerProps = {
  ...React$ElementConfig<typeof AnimatedSpinner>,
  opaque: boolean,
};

const StyledAnimatedSpinner: StyledComponent<StyledAnimatedSpinnerProps> = styled(AnimatedSpinner)`
  position: absolute;
  background: ${DSToken.BACKGROUND_COLOR_PRIMARY};
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  opacity: ${props => (props.opaque ? 1 : 0.66)};
`;

const ButtonGroup: StyledComponent<{}> = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const getValidators = () => ({ address }) => ({
  name: Form.validators.required,
  ...AddressForm.getValidators(address),
});

export type UnwrappedProps = {
  stripe: ?{
    createToken: (tokenData: { type?: string }) => Promise<{
      token?: Object,
      error?: Object,
    }>,
  },
  onStripeFieldsReady: () => void,
  address: Address,
  hideCancelButton: boolean,
  onToggleForm: () => void,
  isSubmitting: boolean,
  onSubmit: (*) => Promise<boolean>,
  nestedForm: boolean,
  supportedCountryCodes: Array<string>,
  centerButtons?: boolean,
  saveButtonText?: string,
};

export type State = {
  stripeValidationErrors: {
    cardNumber: ?string,
    cardExpiry: ?string,
    cardCvc: ?string,
  },
  stripeFieldsToLoad: number,
  stripeError: ?string,
  usePrimaryAddress: boolean,
};

type FormData = { name: string, address: Address };
type StripeElementTypes = 'cardNumber' | 'cardExpiry' | 'cardCvc';
// NOTE(Ryan) Not sure why eslint thought the below type was the Props /shrug
/* eslint-disable react/no-unused-prop-types */
export type StripeChangeEvent = {
  elementType: StripeElementTypes,
  empty: boolean,
  complete: boolean,
  error?: {
    message: string,
    code: string,
    type: 'validation_error',
  },
  brand?: 'visa' | 'mastercard' | 'amex' | 'discover' | 'diners' | 'jcb' | 'unionpay' | 'unknown',
};
/* eslint-enable react/no-unused-prop-types */

export class UnwrappedStripeCreditCardForm extends Component<UnwrappedProps, State> {
  isCreatingDSToken: boolean;

  static defaultProps = {
    onStripeFieldsReady: () => {},
    address: addressFactory(),
    hideCancelButton: false,
    onToggleForm: () => {},
    isSubmitting: false,
    nestedForm: false,
    centerButtons: false,
  };

  constructor(props: UnwrappedProps) {
    super(props);
    // NOTE(Ryan) this .bind() is necessary, otherwise `this` is undefined in handleSubmit()
    //            /shrug babel weirdness?
    // $FlowFixMe: flow thinks this.handleSubmit isn't writable, it is wrong: https://github.com/facebook/flow/issues/5874
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  state = {
    stripeValidationErrors: {
      cardNumber: null,
      cardExpiry: null,
      cardCvc: null,
    },
    stripeFieldsToLoad: 3,
    stripeError: null,
    usePrimaryAddress: isValidAddress(this.props.address),
  };

  isCreatingDSToken = false;

  async handleSubmit(formData: FormData) {
    if (this.isCreatingDSToken) return;
    this.isCreatingDSToken = true;
    this.setState(() => ({ stripeError: null }));

    // https://stripe.com/docs/stripe-js/reference#stripe-create-token
    const { name, address } = formData;
    const tokenData = snakeify({
      name,
      addressLine1: address.addressLine1,
      addressLine2: address.addressLine2,
      addressCity: address.city,
      addressState: address.state,
      addressZip: address.zip,
      addressCountry: address.countryCode,
    });

    const { stripe, onSubmit } = this.props;

    try {
      if (!stripe) throw new Error('Stripe.js Not Found: Please refresh the page and try again.');
      // $FlowFixMe flow complains about error not being in the Promise return type but it clearly is.
      const { token, error } = await stripe.createToken(tokenData);
      this.isCreatingDSToken = false;
      if (error) throw error;
      const isSuccessful = await onSubmit({
        ...formData,
        nonce: token.id,
        formVersion: 'stripe',
        cardholderName: name,
      });
      if (isSuccessful) {
        this.props.onToggleForm();
      }
    } catch (err) {
      this.isCreatingDSToken = false;
      if (err.type !== 'validation_error') {
        reportError(err);
        this.setState(() => ({ stripeError: err.message }));
      }
    }
  }

  handleStripeFieldChange = ({ elementType, error }: StripeChangeEvent) => {
    if (error) {
      this.setState(state => ({
        stripeValidationErrors: { ...state.stripeValidationErrors, [elementType]: error.message },
      }));
    } else {
      this.setState(state => ({
        stripeValidationErrors: { ...state.stripeValidationErrors, [elementType]: null },
      }));
    }
  };

  handleStripeFieldReady = () => {
    const { onStripeFieldsReady } = this.props;

    this.setState(({ stripeFieldsToLoad }) => {
      const newNumFieldsLoaded = stripeFieldsToLoad - 1;
      if (newNumFieldsLoaded === 0) {
        onStripeFieldsReady();
      }
      return { stripeFieldsToLoad: newNumFieldsLoaded };
    });
  };

  handleAddressCheckboxChange = () =>
    this.setState(state => ({
      usePrimaryAddress: !state.usePrimaryAddress,
    }));

  render() {
    const {
      stripeValidationErrors,
      stripeFieldsToLoad,
      stripeError,
      usePrimaryAddress,
    } = this.state;
    const {
      address: propsAddress,
      hideCancelButton,
      onToggleForm,
      isSubmitting,
      nestedForm,
      supportedCountryCodes,
      centerButtons,
      saveButtonText,
    } = this.props;
    const buttonGroupStyle = centerButtons ? { justifyContent: 'center' } : {};
    return (
      <StyledForm
        onSubmit={this.handleSubmit}
        validators={getValidators()}
        initialData={{ name: '', address: propsAddress }}
        data-qa-id="credit-card-forms-add-card"
        nested={nestedForm}
      >
        {form => (
          <Fragment>
            {stripeError && <Alert severity={Alert.Severity.DANGER}>{stripeError}</Alert>}
            <StripeCreditCardNameField
              value={form.data.name}
              onChange={name => form.onChange({ name })}
              validationError={form.validationErrors.name}
            />
            <CreditCardFormFlexWrapper>
              <StripeCreditCardNumberField
                validationError={stripeValidationErrors.cardNumber}
                onChange={this.handleStripeFieldChange}
                onReady={this.handleStripeFieldReady}
              />
              <CVCExpiryResponsiveRow>
                <StripeCreditCardExpiryField
                  validationError={stripeValidationErrors.cardExpiry}
                  onChange={this.handleStripeFieldChange}
                  onReady={this.handleStripeFieldReady}
                />
                <StripeCreditCardCVCField
                  validationError={stripeValidationErrors.cardCvc}
                  onChange={this.handleStripeFieldChange}
                  onReady={this.handleStripeFieldReady}
                />
              </CVCExpiryResponsiveRow>
            </CreditCardFormFlexWrapper>
            {isValidAddress(propsAddress) && (
              <AddressCheckbox
                onChange={this.handleAddressCheckboxChange}
                address={propsAddress}
                isChecked={usePrimaryAddress}
              />
            )}
            {!usePrimaryAddress && (
              <AddressForm
                address={form.data.address}
                onChange={address => form.onChange({ address })}
                validationErrors={form.validationErrors}
                supportedCountryCodes={supportedCountryCodes}
              />
            )}
            {stripeFieldsToLoad !== 0 || isSubmitting ? (
              <StyledAnimatedSpinner
                data-qa-id="credit-card-forms-spinner"
                opaque={!isSubmitting}
              />
            ) : null}
            <ButtonGroup style={buttonGroupStyle}>
              {!hideCancelButton && (
                <Button buttonType={ButtonType.TEXT} onClick={onToggleForm}>
                  <Trans>Cancel</Trans>
                </Button>
              )}
              <LoadingButton
                disabled={isSubmitting || stripeFieldsToLoad !== 0}
                loading={isSubmitting}
                buttonType={ButtonType.PRIMARY}
                type={nestedForm ? 'button' : 'submit'}
                onClick={form.onSubmit}
                data-qa-id="credit-card-buttons-save"
              >
                {saveButtonText || <Trans>Save Card</Trans>}
              </LoadingButton>
            </ButtonGroup>
          </Fragment>
        )}
      </StyledForm>
    );
  }
}

const StripeCreditCardForm = injectStripe(UnwrappedStripeCreditCardForm);

export type Props = {
  ...$Diff<ElementConfig<typeof StripeProvider>, { children: * }>,
  language: string,
};

export default ({ language, onStripeLoaded, ...other }: Props) => (
  <StripeProvider language={language} onStripeLoaded={onStripeLoaded}>
    <StripeCreditCardForm {...other} />
  </StripeProvider>
);



// WEBPACK FOOTER //
// ../react-lib/src/components/forms/StripeCreditCardForm/StripeCreditCardForm.jsx